/**
 * Pure geometry helpers for the Dynamic Input HTML overlay.
 *
 * Converts between world coordinates (DXF model space) and CSS-pixel
 * screen coordinates so that the overlay div can be positioned at the
 * right spot relative to the cursor regardless of zoom / pan / Y-flip.
 *
 * The coordinate math mirrors the logic in PrimitiveAtlasStage.tsx
 * (handleCanvasMouseMove lines ~1180-1192):
 *
 *   viewport_x = worldX * zoom + panX
 *   viewport_y = svgY  * zoom + panY   (svgY = stageHeight - worldY  in CAD mode)
 *   screen_x   = viewport_x + stageRect.left
 *   screen_y   = viewport_y + stageRect.top
 *
 * The DI overlay is positioned with `position: absolute` inside the
 * stage shell div, so stageRect.left/top cancel out: we only need the
 * viewport offsets.
 *
 * NOTE: stageHeight (960) is the SVG coordinate height — a compile-time
 * constant shared with PrimitiveAtlasStage. It is NOT the CSS pixel height
 * of the container; the SVG is viewBox-based and the CSS transform scales
 * it. We avoid importing it from PrimitiveAtlasStage to keep this module
 * dependency-free; instead, it can be passed in (defaulting to 960 for
 * the current project).
 */

export type ScreenPoint = { leftPx: number; topPx: number };
export type WorldPoint = { x: number; y: number };
export type CameraState = { zoom: number; panX: number; panY: number };
/** CSS bounding rect of the stage shell element. */
export type StageRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};
export type Offset = { dx: number; dy: number };

/** SVG canvas width (matches PrimitiveAtlasStage stageWidth constant). */
export const SVG_STAGE_WIDTH = 1600;

/** SVG canvas height (matches PrimitiveAtlasStage stageHeight constant). */
export const SVG_STAGE_HEIGHT = 960;

/** Default CSS-pixel offset from the cursor for the pointer input fields. */
export const DEFAULT_POINTER_OFFSET: Offset = { dx: 20, dy: 12 };

/** Default CSS-pixel offset from the cursor for the dynamic prompt pill. */
export const DEFAULT_PROMPT_OFFSET: Offset = { dx: 20, dy: 40 };

/**
 * Convert a DXF world-space point to CSS-pixel screen coordinates.
 *
 * The returned position is relative to the top-left of the stage shell
 * div (the ref we receive from PrimitiveAtlasStage) so it can be used
 * directly as `style.left` / `style.top` on an `position:absolute` child.
 *
 * @param world      - DXF world coordinates (x right, y up in CAD mode)
 * @param camera     - current zoom / pan
 * @param stageRect  - bounding rect of the stage shell element
 * @param isCadMode  - true when coordinateSystem === "cad" (Y-axis flipped)
 * @param offset     - additional CSS-pixel offset applied AFTER projection
 *                     (used to push the overlay below-right of the cursor)
 * @param svgHeight  - optional override for the SVG viewBox height (default 960)
 */
export function screenFromWorld(
  world: WorldPoint,
  camera: CameraState,
  stageRect: StageRect,
  isCadMode: boolean,
  offset?: Offset,
  svgHeight: number = SVG_STAGE_HEIGHT,
  svgWidth: number = SVG_STAGE_WIDTH,
): ScreenPoint {
  // Convert world → SVG viewBox space (Y-flip in CAD mode).
  const svgX = world.x;
  const svgY = isCadMode ? svgHeight - world.y : world.y;

  // Position inside the SVG viewBox after camera transform. The viewBox is
  // 0..svgWidth × 0..svgHeight — these are SVG units, NOT CSS pixels yet.
  const viewboxX = svgX * camera.zoom + camera.panX;
  const viewboxY = svgY * camera.zoom + camera.panY;

  // The SVG element has CSS size stageRect.width × stageRect.height and a
  // viewBox of svgWidth × svgHeight. Default preserveAspectRatio is
  // xMidYMid meet, so the content is uniformly scaled; using the width
  // ratio as the scalar works while the shell preserves the 1600×960
  // aspect ratio. Overlay children are `position: absolute` inside the
  // stage shell, so we return pixel offsets relative to its top-left.
  const pxScaleX = stageRect.width / svgWidth;
  const pxScaleY = stageRect.height / svgHeight;

  const cssX = viewboxX * pxScaleX;
  const cssY = viewboxY * pxScaleY;

  const dx = offset?.dx ?? 0;
  const dy = offset?.dy ?? 0;

  return {
    leftPx: cssX + dx,
    topPx: cssY + dy,
  };
}

/**
 * Convert a CSS-pixel viewport coordinate (relative to stage shell) to a
 * DXF world-space point.  Inverse of `screenFromWorld`.
 *
 * @param screen     - CSS-pixel position relative to stage shell
 * @param camera     - current zoom / pan
 * @param stageRect  - bounding rect of the stage shell element (not used for
 *                     the inverse but kept for symmetry / future use)
 * @param isCadMode  - true when coordinateSystem === "cad"
 * @param svgHeight  - optional override for the SVG viewBox height (default 960)
 */
export function worldFromScreen(
  screen: { x: number; y: number },
  camera: CameraState,
  stageRect: StageRect,
  isCadMode: boolean,
  svgHeight: number = SVG_STAGE_HEIGHT,
  svgWidth: number = SVG_STAGE_WIDTH,
): WorldPoint {
  const pxScaleX = stageRect.width / svgWidth;
  const pxScaleY = stageRect.height / svgHeight;
  const viewboxX = screen.x / pxScaleX;
  const viewboxY = screen.y / pxScaleY;
  const svgX = (viewboxX - camera.panX) / camera.zoom;
  const svgY = (viewboxY - camera.panY) / camera.zoom;
  const worldX = svgX;
  const worldY = isCadMode ? svgHeight - svgY : svgY;
  return { x: worldX, y: worldY };
}

/**
 * Compute the polar distance and angle (degrees, 0 = East, CCW positive)
 * between a reference point and a cursor point in world coordinates.
 *
 * AutoCAD parity: angle is measured CCW from positive X axis.
 */
export function polarFromPoints(
  reference: WorldPoint,
  cursor: WorldPoint,
): { dist: number; angleDeg: number } {
  const dx = cursor.x - reference.x;
  const dy = cursor.y - reference.y;
  const dist = Math.hypot(dx, dy);
  let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (angleDeg < 0) angleDeg += 360;
  return { dist, angleDeg };
}

/**
 * Optional dim-input kind hint. When set, `formatLiveValues` overrides the
 * `mode`-derived secondary placeholder so the live tooltip / dim-field
 * placeholder reflects the field's actual semantic instead of always
 * showing the polar-angle of the cursor relative to the reference point.
 *
 * AutoCAD parity:
 *  - `width-height` (RECTANGLE / REVCLOUD-RECT / MTEXT-second-corner /
 *    HATCH-second-corner / VIEWPORT-second-corner / ARRAYRECT-spacing /
 *    CHAMFER-distances / WIPEOUT-second-corner) — secondary slot is
 *    the height value (cursor.y − ref.y), in drawing units. AutoCAD
 *    shows the live height as a length, NEVER as an angle.
 *  - `distance-angle` (LINE-next-point / PLINE-next-point / ARC-end) —
 *    secondary slot is the polar angle (degrees), as today.
 *  - All other kinds — single-field; secondary slot is unused but the
 *    pointer-fields fallback (cartesian/polar) still consumes it.
 *
 * Kept optional and last-arg so existing callers (pointer-input mode,
 * read-only tooltip, tests) keep working without changes.
 */
export type DimKindHint =
  | "width-height"
  | "distance-angle"
  | "length"
  | "radius"
  | "diameter"
  | "rotation"
  | "angle"
  | "none";

/**
 * Format a polar or cartesian live value string for the read-only tooltip
 * that is shown before the user starts typing.
 *
 * `dimKind` (optional): when supplied, the secondary slot is rendered to
 * match the field's intrinsic semantic (height for `width-height`,
 * polar-angle for `distance-angle`). Without `dimKind` the function
 * preserves its original cartesian/polar `mode`-driven behavior, used by
 * the Pointer Input fallback and the read-only tooltip.
 */
export function formatLiveValues(
  cursor: WorldPoint,
  reference: WorldPoint | null,
  mode: "polar" | "cartesian",
  format: "relative" | "absolute",
  dimKind?: DimKindHint,
): { primary: string; secondary: string } {
  // ── AutoCAD-parity override for kind="width-height" ──
  // The secondary live-value MUST be a length (height delta in drawing
  // units), not the polar angle that the `mode` switch below would
  // otherwise produce. Primary is the width (X delta) when relative, or
  // the absolute X coordinate otherwise — AutoCAD shows the cursor's
  // X-projection there during rectangle / mtext / revcloud /
  // hatch-second-corner / viewport-second-corner / arrayrect-spacing
  // / chamfer-distances flow, regardless of DYNPICOORDS / DYNPIFORMAT.
  if (dimKind === "width-height") {
    if (reference) {
      return {
        primary: Math.abs(cursor.x - reference.x).toFixed(3),
        secondary: Math.abs(cursor.y - reference.y).toFixed(3),
      };
    }
    return {
      primary: cursor.x.toFixed(3),
      secondary: cursor.y.toFixed(3),
    };
  }

  // ── kind="distance-angle" — primary=length, secondary=polar angle.
  // Independent of cartesian / polar `mode` because LINE / PLINE /
  // ARC-end always show distance + angle in AutoCAD's Dimension Input.
  if (dimKind === "distance-angle") {
    const ref = reference ?? { x: 0, y: 0 };
    const { dist, angleDeg } = polarFromPoints(ref, cursor);
    return {
      primary: dist.toFixed(3),
      secondary: angleDeg.toFixed(1) + "\u00b0",
    };
  }

  if (mode === "polar") {
    if (reference && format === "relative") {
      const { dist, angleDeg } = polarFromPoints(reference, cursor);
      return {
        primary: dist.toFixed(3),
        secondary: angleDeg.toFixed(1) + "\u00b0",
      };
    }
    // Absolute polar: distance from origin.
    const { dist, angleDeg } = polarFromPoints({ x: 0, y: 0 }, cursor);
    return {
      primary: dist.toFixed(3),
      secondary: angleDeg.toFixed(1) + "\u00b0",
    };
  }
  // Cartesian
  if (reference && format === "relative") {
    return {
      primary: (cursor.x - reference.x).toFixed(3),
      secondary: (cursor.y - reference.y).toFixed(3),
    };
  }
  return {
    primary: cursor.x.toFixed(3),
    secondary: cursor.y.toFixed(3),
  };
}
